import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Users, UserPlus, Mail, MoreVertical, Crown, Shield,
  ChefHat, UserCheck, Calculator, Loader2, Copy, Trash2,
  AlertCircle, Share2, Wallet, FileSpreadsheet, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import TeamBroadcastCard from "../components/settings/TeamBroadcastCard";

// ─── Constants ────────────────────────────────────────────────────────
const ROLES = [
  { value: "manager",      label: "Encargado",      desc: "Todo excepto configuracion del restaurante", icon: Shield,     color: "text-indigo-600",  bg: "bg-indigo-50 border-indigo-200" },
  { value: "kitchen",      label: "Cocina",          desc: "Inventario, recetas, listas de compra",      icon: ChefHat,    color: "text-orange-700",  bg: "bg-orange-50 border-orange-200" },
  { value: "waiter",       label: "Camarero",        desc: "Solo menus (solo lectura)",                  icon: UserCheck,  color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  { value: "accountant",   label: "Contabilidad",    desc: "Dashboard, previsiones, TPV",                icon: Calculator, color: "text-blue-600",    bg: "bg-blue-50 border-blue-200" },
  { value: "social_media", label: "Social Media",    desc: "Gestion de redes sociales",                  icon: Share2,     color: "text-pink-600",    bg: "bg-pink-50 border-pink-200" },
];

const ROLE_MAP = Object.fromEntries(ROLES.map(r => [r.value, r]));

function RoleBadge({ role }) {
  if (role === "owner") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        <Crown className="w-3 h-3" /> Propietario
      </span>
    );
  }
  const r = ROLE_MAP[role];
  if (!r) return <span className="text-xs text-muted-foreground">{role}</span>;
  const Icon = r.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${r.bg} ${r.color}`}>
      <Icon className="w-3 h-3" /> {r.label}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────
export default function TeamPage() {
  const { currentRestaurant, currentRole } = useOutletContext();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);

  // Invite dialog
  const [showInvite, setShowInvite] = useState(false);
  const [invEmail, setInvEmail]     = useState("");
  const [invRole, setInvRole]       = useState("manager");
  const [inviting, setInviting]     = useState(false);
  const [inviteUrl, setInviteUrl]   = useState(null);

  // Change role dialog
  const [changingMember, setChangingMember] = useState(null);
  const [newRole, setNewRole]               = useState("");
  const [savingRole, setSavingRole]         = useState(false);

  const rid = currentRestaurant?.restaurant_id;

  const loadMembers = useCallback(async () => {
    if (!rid) return;
    try {
      setLoading(true);
      const res = await api.request(`/restaurants/${rid}/members`);
      setData(res);
    } catch (e) {
      toast.error("Error al cargar el equipo");
    } finally {
      setLoading(false);
    }
  }, [rid]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleInvite = async () => {
    if (!invEmail.trim()) { toast.error("Introduce un email"); return; }
    setInviting(true);
    try {
      const res = await api.request(`/restaurants/${rid}/members/invite`, {
        method: "POST",
        body: JSON.stringify({ email: invEmail.trim(), role: invRole }),
      });
      toast.success(res.message);
      setInviteUrl(res.invite_url || null);
      setInvEmail("");
      loadMembers();
    } catch (e) {
      toast.error(e.message || "Error al enviar la invitación");
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async () => {
    if (!changingMember || !newRole) return;
    setSavingRole(true);
    try {
      await api.request(`/restaurants/${rid}/members/${changingMember.member_id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role: newRole }),
      });
      toast.success("Rol actualizado");
      setChangingMember(null);
      loadMembers();
    } catch (e) {
      toast.error(e.message || "Error al cambiar el rol");
    } finally {
      setSavingRole(false);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`¿Eliminar a ${member.name || member.email} del equipo?`)) return;
    try {
      await api.request(`/restaurants/${rid}/members/${member.member_id}`, { method: "DELETE" });
      toast.success("Miembro eliminado");
      loadMembers();
    } catch (e) {
      toast.error(e.message || "Error al eliminar el miembro");
    }
  };

  const handleCancelInvite = async (inv) => {
    try {
      await api.request(`/restaurants/${rid}/invitations/${inv.invitation_id}`, { method: "DELETE" });
      toast.success("Invitación cancelada");
      loadMembers();
    } catch (e) {
      toast.error("Error al cancelar la invitación");
    }
  };

  const canManage = currentRole === "owner" || currentRole === "manager";

  if (!currentRestaurant) return null;

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Equipo</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gestiona los miembros del equipo de {currentRestaurant.name}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => { setShowInvite(true); setInviteUrl(null); }} data-testid="btn-open-invite">
            <UserPlus className="w-4 h-4 mr-2" />
            Invitar miembro
          </Button>
        )}
      </div>

      {/* Roles reference card */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {ROLES.map(r => {
          const Icon = r.icon;
          return (
            <div key={r.value} className={`rounded-xl border p-3 ${r.bg}`}>
              <div className={`flex items-center gap-2 font-medium text-sm ${r.color} mb-1`}>
                <Icon className="w-4 h-4" />
                {r.label}
              </div>
              <p className="text-xs text-muted-foreground">{r.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Team broadcast (owner/manager only) — moved here from Dashboard so
          the action lives next to the rest of team management. */}
      {canManage && rid && (
        <Card data-testid="team-broadcast-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-amber-700" />
              Comunicar al equipo
            </CardTitle>
            <CardDescription>
              Envía un aviso puntual a todos los móviles del equipo con la app instalada.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TeamBroadcastCard restaurantId={rid} compact />
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-7 h-7 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Active members */}
          <Card data-testid="members-list">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Miembros activos ({(data?.members?.length || 0) + 1})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Owner row */}
              {data?.owner && (
                <MemberRow
                  member={data.owner}
                  isOwner
                  canManage={false}
                />
              )}
              {/* Member rows */}
              {data?.members?.map(m => (
                <MemberRow
                  key={m.member_id}
                  member={m}
                  canManage={canManage && currentRole === "owner"}
                  restaurantId={data?.restaurant_id || data?.owner?.restaurant_id}
                  onChangeRole={() => { setChangingMember(m); setNewRole(m.role); }}
                  onRemove={() => handleRemove(m)}
                  onFlagChange={async (flagKey, value) => {
                    try {
                      const rid = data?.restaurant_id || currentRestaurant?.restaurant_id;
                      await api.request(`/restaurants/${rid}/members/${m.member_id}/flags`, {
                        method: "PUT",
                        body: JSON.stringify({ [flagKey]: value }),
                      });
                      toast.success("Permisos actualizados");
                      load();
                    } catch (err) {
                      toast.error(err?.message || "Error al actualizar permisos");
                    }
                  }}
                />
              ))}
              {(!data?.members || data.members.length === 0) && (
                <div className="px-6 py-4 text-sm text-muted-foreground border-t">
                  Solo está el propietario. Invita a tu equipo con el botón de arriba.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending invitations */}
          {data?.pending_invitations?.length > 0 && (
            <Card data-testid="pending-invitations">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-500" />
                  Invitaciones pendientes ({data.pending_invitations.length})
                </CardTitle>
                <CardDescription className="text-xs">
                  Caducan a las 72 h de ser enviadas
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {data.pending_invitations.map(inv => (
                  <div key={inv.invitation_id}
                       className="flex items-center justify-between px-6 py-3 border-t first:border-t-0 hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <Mail className="w-4 h-4 text-amber-700" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{inv.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Invitado por {inv.invited_by_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <RoleBadge role={inv.role} />
                      {canManage && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleCancelInvite(inv)}
                                data-testid={`cancel-invite-${inv.invitation_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary" />
              Invitar miembro al equipo
            </DialogTitle>
            <DialogDescription>
              El invitado recibirá un email con un enlace para unirse. El enlace caduca en 72 h.
            </DialogDescription>
          </DialogHeader>
          {inviteUrl ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/40">
                <p className="text-xs font-mono flex-1 break-all text-muted-foreground">{inviteUrl}</p>
                <Button size="sm" variant="outline" className="flex-shrink-0"
                        onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success("Enlace copiado"); }}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                También se ha enviado un email. Puedes copiar y compartir este enlace directamente.
              </p>
              <Button className="w-full" onClick={() => setShowInvite(false)}>Cerrar</Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-2">
                <div>
                  <Label htmlFor="inv-email-dialog" className="text-sm">Email del nuevo miembro *</Label>
                  <Input id="inv-email-dialog" type="email" value={invEmail}
                         onChange={e => setInvEmail(e.target.value)}
                         placeholder="empleado@mirestaurante.com" className="mt-1.5"
                         data-testid="invite-email-input" />
                </div>
                <div>
                  <Label className="text-sm">Rol asignado *</Label>
                  <Select value={invRole} onValueChange={setInvRole}>
                    <SelectTrigger className="mt-1.5" data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          <div>
                            <span className="font-medium">{r.label}</span>
                            <span className="text-muted-foreground text-xs ml-2">{r.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowInvite(false)} disabled={inviting}>Cancelar</Button>
                <Button onClick={handleInvite} disabled={inviting || !invEmail.trim()}
                        data-testid="btn-send-invite">
                  {inviting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
                    : <><Mail className="w-4 h-4 mr-2" />Enviar invitación</>
                  }
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!changingMember} onOpenChange={() => setChangingMember(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cambiar rol</DialogTitle>
            <DialogDescription>
              Cambia el rol de <strong>{changingMember?.name || changingMember?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="change-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setChangingMember(null)} disabled={savingRole}>Cancelar</Button>
            <Button onClick={handleChangeRole} disabled={savingRole || newRole === changingMember?.role}
                    data-testid="btn-save-role">
              {savingRole ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Member Row ───────────────────────────────────────────────────────
function MemberRow({ member, isOwner = false, canManage = false, onChangeRole, onRemove, onFlagChange }) {
  const initials = (member.name || member.email || "?").slice(0, 2).toUpperCase();
  const flags = member.flags || {};
  const showFlags = canManage && !isOwner && (member.role === "waiter" || member.role === "accountant");
  return (
    <div className="border-t first:border-t-0 hover:bg-muted/30 transition-colors">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
            {initials}
          </div>
          <div>
            <p className="text-sm font-medium">{member.name || "—"}</p>
            <p className="text-xs text-muted-foreground">{member.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RoleBadge role={member.role} />
          {canManage && !isOwner && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                        data-testid={`member-menu-${member.member_id}`}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onChangeRole}>
                  <Shield className="w-4 h-4 mr-2" /> Cambiar rol
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={onRemove}>
                  <Trash2 className="w-4 h-4 mr-2" /> Eliminar del equipo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {showFlags && (
        <div className="px-6 pb-3 -mt-1 flex flex-wrap gap-2">
          {member.role === "waiter" && (
            <FlagToggle
              icon={Wallet}
              label="Permitir operar caja"
              tooltip="El camarero podrá abrir/cerrar caja y registrar ventas además de tomar comandas"
              testid={`flag-cashier-${member.member_id}`}
              checked={!!flags.cashier}
              onChange={(v) => onFlagChange?.("cashier", v)}
            />
          )}
          {member.role === "accountant" && (
            <FlagToggle
              icon={FileSpreadsheet}
              label="Permitir editar contabilidad"
              tooltip="Por defecto el contable es de sólo lectura. Activar para que pueda modificar facturas y categorías."
              testid={`flag-edit-acct-${member.member_id}`}
              checked={!!flags.can_edit_accounting}
              onChange={(v) => onFlagChange?.("can_edit_accounting", v)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function FlagToggle({ icon: Icon, label, tooltip, testid, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange?.(!checked)}
      title={tooltip}
      data-testid={testid}
      className={`inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
        checked
          ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
          : "bg-muted/50 border-transparent text-muted-foreground hover:bg-muted"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      <span className={`w-7 h-3.5 rounded-full ml-1 relative transition-colors ${checked ? "bg-emerald-600" : "bg-muted-foreground/30"}`}>
        <span className={`absolute top-0 left-0 w-3.5 h-3.5 rounded-full bg-card shadow transition-transform ${checked ? "translate-x-3.5" : ""}`} />
      </span>
    </button>
  );
}
